import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { resolveSharePointUrl, type ResolvedSharePointSite } from '@/lib/sharepoint-api';
import { Loader2 } from 'lucide-react';

interface SitePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialUrl?: string;
  onApply: (result: {
    siteId: string;
    driveId: string;
    siteName?: string;
    driveName?: string;
    siteUrl?: string;
    driveUrl?: string;
  }) => void;
}

export function SitePickerDialog({
  open,
  onOpenChange,
  initialUrl,
  onApply
}: SitePickerDialogProps) {
  const [url, setUrl] = useState(initialUrl || '');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [resolved, setResolved] = useState<ResolvedSharePointSite | null>(null);
  const [driveId, setDriveId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const requestId = useRef(0);

  useEffect(() => {
    requestId.current += 1;
    setUrl(initialUrl || '');
    setResolved(null);
    setDriveId('');
    setError('');
    setLoading(false);
    setLoadingMore(false);
    return () => { requestId.current += 1; };
  }, [open, initialUrl]);

  const selectedDrive = useMemo(() => {
    if (!resolved) return undefined;
    return resolved.drives.find(d => d.id === driveId);
  }, [resolved, driveId]);

  const canApply = !!resolved?.site?.id && !!driveId;

  const handleLookup = async () => {
    const currentRequest = ++requestId.current;
    setError('');
    setResolved(null);
    setDriveId('');

    if (!url.trim()) {
      setError('Paste a SharePoint site or library URL.');
      return;
    }

    try {
      setLoading(true);
      const data = await resolveSharePointUrl(url.trim());
      if (currentRequest !== requestId.current) return;
      setResolved(data);
      setDriveId(data.suggestedDriveId || (data.drives[0]?.id ?? ''));
    } catch (e: unknown) {
      if (currentRequest === requestId.current) setError(e instanceof Error ? e.message : 'Failed to resolve URL');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!resolved?.nextLink || loadingMore) return;
    const currentRequest = ++requestId.current;
    setLoadingMore(true);
    setError('');
    try {
      const data = await resolveSharePointUrl(resolved.inputUrl, resolved.nextLink);
      if (currentRequest !== requestId.current) return;
      // Native pages can overlap during provider changes; keep one entry per id.
      const drives = new Map(resolved.drives.map(drive => [drive.id, drive]));
      for (const drive of data.drives) drives.set(drive.id, drive);
      setResolved({ ...resolved, drives: [...drives.values()], nextLink: data.nextLink });
    } catch (e: unknown) {
      if (currentRequest === requestId.current) setError(e instanceof Error ? e.message : 'Failed to load libraries');
    } finally {
      if (currentRequest === requestId.current) setLoadingMore(false);
    }
  };

  const handleApply = () => {
    if (!resolved) return;
    onApply({
      siteId: resolved.site.id,
      driveId,
      siteName: resolved.site.displayName || resolved.site.name,
      driveName: selectedDrive?.name,
      siteUrl: resolved.site.webUrl,
      driveUrl: selectedDrive?.webUrl
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Pick SharePoint Site & Library</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="sp-url">SharePoint URL</Label>
            <div className="flex gap-2">
              <Input
                id="sp-url"
                value={url}
                onChange={(e) => {
                  requestId.current += 1;
                  setUrl(e.target.value);
                  setResolved(null);
                  setDriveId('');
                  setError('');
                  setLoading(false);
                  setLoadingMore(false);
                }}
                placeholder="Paste a SharePoint site or library URL"
              />
              <Button type="button" onClick={handleLookup} disabled={loading || loadingMore}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Lookup
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Example: https://tenant.sharepoint.com/sites/SiteName/Shared%20Documents
            </p>
          </div>

          {error && (
            <div className="text-sm text-destructive">
              {error}
            </div>
          )}

          {resolved && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="text-sm">
                <div className="font-medium">Site</div>
                <div className="text-muted-foreground break-all">
                  {resolved.site.displayName || resolved.site.name || resolved.site.id}
                  {resolved.site.webUrl ? ` · ${resolved.site.webUrl}` : ''}
                </div>
                <div className="text-muted-foreground break-all">
                  siteId: {resolved.site.id}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Document Library</Label>
                <Select value={driveId} onValueChange={setDriveId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a library" />
                  </SelectTrigger>
                  <SelectContent>
                    {resolved.drives.map((drive) => (
                      <SelectItem key={drive.id} value={drive.id}>
                        {drive.name || drive.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {resolved.nextLink && (
                  <Button type="button" variant="outline" onClick={handleLoadMore} disabled={loadingMore}>
                    {loadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Load more libraries
                  </Button>
                )}
                {driveId && (
                  <div className="text-xs text-muted-foreground break-all">
                    driveId: {driveId}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApply} disabled={!canApply}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
